import {Spin, Tooltip, Typography} from "antd";
import dayjs from "dayjs";
import React, {useCallback, useContext, useMemo} from 'react';
import {OrganisationContext} from "utils/organisationContext";

const {Text} = Typography;

interface IDateTimeRendererProps {
    date: dayjs.Dayjs | number | string;
    format?: string | { date: string, time: string };
    onlyDate?: boolean;
    onlyTime?: boolean;
    fallback?: string;
    className?: string;

    /**
     * If true, the tooltip will show the time from now.
     * If a string is provided, it will be used as the tooltip content.
     * If a React node is provided, it will be rendered as the tooltip content.
     */
    tooltip?: boolean | string | React.ReactNode;
}

interface ITimezoneDateTimeRendererProps extends IDateTimeRendererProps {
    timezone: string;
}

export const OrganizationalDateTimeRenderer: React.FC<IDateTimeRendererProps> = (props) => {
    const organisation = useContext(OrganisationContext);

    return (
        <span style={{display: 'inline-block'}}>
          <Spin spinning={!organisation}>
            {
                organisation && (
                    <TimezoneOrientedDateTimeRenderer timezone={organisation.timezone} {...props}/>
                )
            }
          </Spin>
        </span>
    );
};

export const TimezoneOrientedDateTimeRenderer: React.FC<ITimezoneDateTimeRendererProps> = ({
                                                                                               timezone,
                                                                                               date,
                                                                                               onlyDate,
                                                                                               onlyTime,
                                                                                               format,
                                                                                               className,
                                                                                               tooltip = false,
                                                                                               fallback = '-'
                                                                                           }) => {

    const dateValue = dayjs(date).tz(timezone);

    const dateTimeFormat = useMemo(() => {
        const defaultFormatDate = `MM/DD/YYYY`;
        const defaultFormatTime = `hh:mm:ss A`;

        const formatDate = (format && typeof format === 'object' && format.date) || defaultFormatDate;
        const formatTime = (format && typeof format === 'object' && format.time) || defaultFormatTime;

        if (format && typeof format === 'string') {
            return format;
        }

        let formatTemplate = `${formatDate}, ${formatTime}`;
        if (onlyDate !== null && onlyDate) {
            formatTemplate = formatDate;
        } else if (onlyTime !== null && onlyTime) {
            formatTemplate = formatTime;
        }
        return formatTemplate;
    }, [format, onlyDate, onlyTime]);

    const coreComponent = useMemo(() => (
        <>
            <Text className={className}>
                {
                    (date && dateValue.isValid())
                        ? dateValue.format(dateTimeFormat)
                        : fallback
                }
            </Text>
        </>
    ), [className, date, dateValue, dateTimeFormat, fallback]);


    const getTooltip = useCallback<() => React.ReactNode>(() => {
        if (typeof tooltip === 'string') {
            return tooltip;
        } else if (React.isValidElement(tooltip)) {
            return tooltip;
        } else if (tooltip === true) {
            switch (typeof date) {
                default:
                    const dayjsObject = dayjs(date);
                    return date && dayjsObject.isValid()
                        ? dayjsObject.fromNow()
                        : null;
            }
        }
        return null;
    }, [date, tooltip]);

    return !tooltip ? coreComponent : (
        <>
            <Tooltip
                title={getTooltip()}
            >
                {coreComponent}
            </Tooltip>
        </>
    );
}
